import React from 'react';
import {useLoaderData} from 'react-router-dom';
import {Col, Row, Tab} from "react-bootstrap";
import PractitionerInfo from "./PractitionerInfo";
import graphql from "babel-plugin-relay/macro";
import {usePreloadedQuery} from "react-relay";
import PractitionerIdentifiers from "./identifiers/PractitionerIdentifiers";
import {useUser} from "../../hooks/useUser";
import PractitionerLanguages from "./languages/PractitionerLanguages";
import PractitionerVersions from "./versions/PractitionerVersions";
import DeletePanel from "../../components/common/repeated-page-panels/DeletePanel";
import PractitionerContactPoints from "./contactPoints/PractitionerContactPoints";
import PractitionerLocations from "./locations/PractitionerLocations";
import ProviderBillingAddresses from "../../components/provider/billing-addresses/ProviderBillingAddresses";
import MultiModeDetailsPageHeader from "../../components/common/headers/MultiModeDetailsPageHeader";

const PractitionerDetails = () => {
    const loader = useLoaderData();
    const data = usePreloadedQuery(graphql`
    query PractitionerDetailsQuery($id: ID!) {
        node(id: $id) {
            id
            ... on Practitioner {
                ...PractitionerInfo_Practitioner
                applicableVersion {
                    displayName
                }
                legalContract {
                    id
                    name
                    locations {
                        ...LocationFragments_LocationDropdown
                    }
                    linesOfService {
                        ...ContractLinesOfServiceFragments_LinesOfService
                    }
                    billingGroups {
                        ...BillingGroupFragments_BillingGroupDropdown
                    }
                }
                versions {
                    ...PractitionerVersions_Versions
                }
                languages {
                    ...PractitionerLanguages_Languages
                }
                identifiers {
                    ...PractitionerIdentifiers_Identifiers
                }
                billingAddresses {
                    ...ProviderBillingAddresses_Addresses
                }
                contactPoints {
                    ...PractitionerContactPoints_ContactPoints
                }
                locations {
                    ...PractitionerLocations_Locations
                    ...TermLocationsModal_LocationDropdown
                }
            }
        }
        allIdentifierUseCodes {
            ...Fragments_AllIdentifierUseCodes
        }
        allIdentifierTypes {
            ...Fragments_AllIdentifierTypes
        }
        allGenders {
            ...Fragments_AllGendersDropdown
        }
        allProfessionalDesignations {
            ...Fragments_AllProfessionalDesignationDropdown
        }
        allLanguages {
            ...Fragments_AllLanguagesDropdown
        }
        allEthnicities {
            ...Fragments_AllEthnicitiesDropdown
        }
        allLegalContracts {
            ...ContractFragments_AllContractsDropdown
        }
        allContactPointUseCodes {
            ...Fragments_AllContactPointUseCodesDropdown
        }
        allContactPointSystemCodes {
            ...Fragments_AllContactPointSystemCodesDropdown
        }
    }`, loader.practitionerDetailsQuery);
    const deleteMutation = graphql`
    mutation PractitionerDetails_DeleteMutation($id: ID!) {
        deleteNode(input: {id: $id}) {
            deletedNodeId @deleteRecord
        }
    }`
    const {hasPermission} = useUser();
    return (
        <Tab.Container defaultActiveKey={"current"}>
            <MultiModeDetailsPageHeader iconText={"Practitioner"} iconType={"user-md"} title={data.node.applicableVersion.displayName} />
            <Tab.Content>
                <Tab.Pane eventKey={"current"}>
                    <Row className={"mb-3"}>
                        <PractitionerInfo practitioner={data.node} 
                                          legalContracts={data.allLegalContracts}
                                          displayMode={"current"}
                        />
                    </Row>
                    <Row>
                        <Col>
                            {hasPermission("PractitionerLocation.Read") && <PractitionerLocations practitionerId={data.node.id}
                                                                                                  allLocations={data.node.legalContract.locations}
                                                                                                  allBillingGroups={data.node.legalContract.billingGroups}
                                                                                                  locations={data.node.locations}
                                                                                                  availableLineOfServices={data.node.legalContract.linesOfService}
                                                                                                  displayMode={"current"}
                            />}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {hasPermission("PractitionerIdentifier.Read") &&  <PractitionerIdentifiers practitionerId={data.node.id} 
                                                     identifiers={data.node.identifiers} 
                                                     allIdentifierTypes={data.allIdentifierTypes} 
                                                     allIdentifierUseCodes={data.allIdentifierUseCodes} 
                                                     displayMode={"current"}
                            />}
                        </Col>
                        <Col>
                            {hasPermission("PractitionerLanguage.Read") && <PractitionerLanguages practitionerId={data.node.id}
                                                                                               languages={data.node.languages}
                                                                                               allLanguages={data.allLanguages}
                                                                                               displayMode={"current"}
                            />}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {hasPermission("PractitionerContactPoint.Read") && <PractitionerContactPoints practitionerId={data.node.id}
                                                                                                       contactPoints={data.node.contactPoints}
                                                                                                       systemCodes={data.allContactPointSystemCodes}
                                                                                                       useCodes={data.allContactPointUseCodes}
                                                                                                       displayMode={"current"}
                                />}
                        </Col>
                    </Row>
                </Tab.Pane>
                <Tab.Pane eventKey={"historical"}>
                    <Row className={"mb-3"}>
                        <PractitionerInfo practitioner={data.node} 
                                          legalContracts={data.allLegalContracts}
                        />
                    </Row>
                    <Row>
                        <Col>
                            <PractitionerVersions practitionerId={data.node.id} 
                                                  versions={data.node.versions} 
                                                  allEthnicities={data.allEthnicities} 
                                                  allGenders={data.allGenders} 
                                                  allProfessionalDesignations={data.allProfessionalDesignations} 
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {hasPermission("PractitionerLocation.Read") && <PractitionerLocations practitionerId={data.node.id}
                                                                                                  allLocations={data.node.legalContract.locations}
                                                                                                  allBillingGroups={data.node.legalContract.billingGroups}
                                                                                                  locations={data.node.locations}
                                                                                                  availableLineOfServices={data.node.legalContract.linesOfService}
                                                                                                  
                            />}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {hasPermission("PractitionerIdentifier.Read") &&  <PractitionerIdentifiers practitionerId={data.node.id} 
                                                     identifiers={data.node.identifiers} 
                                                     allIdentifierTypes={data.allIdentifierTypes} 
                                                     allIdentifierUseCodes={data.allIdentifierUseCodes} 
                            />}
                        </Col>
                        <Col>
                            {hasPermission("PractitionerLanguage.Read") && <PractitionerLanguages practitionerId={data.node.id}
                                                                                               languages={data.node.languages}
                                                                                               allLanguages={data.allLanguages}
                            />}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {hasPermission("PractitionerContactPoint.Read") && <PractitionerContactPoints practitionerId={data.node.id}
                                                                                                       contactPoints={data.node.contactPoints}
                                                                                                       systemCodes={data.allContactPointSystemCodes}
                                                                                                       useCodes={data.allContactPointUseCodes}
                                />}
                        </Col>
                    </Row>
                    {hasPermission("Practitioner.Delete") && 
                        <DeletePanel mutation={deleteMutation} 
                                     redirectUrl={"/practitioners"} 
                                     thingToDelete={"Practitioner"} 
                                     id={data.node.id}>
                            In order to delete a practitioner, there must be only 1 version and no other related data.
                    </DeletePanel>}
                </Tab.Pane>
            </Tab.Content>
        </Tab.Container>
    );
};

export default PractitionerDetails;
